import axios from "axios";
import { API_CONFIG } from "../config/api";
import {
    Expense,
    ExpenseCreate,
    ExpenseUpdate,
    ExpenseCategory,
    ExpenseCategoryCreate,
    ExpenseCategoryUpdate,
    ExpenseSearchParams,
    ExpenseStatisticsResponse,
    PaginatedResponse,
} from "../types";

class ExpenseService {
    private getAuthHeader() {
        const token = localStorage.getItem("access_token");
        return {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
    }

    // ============= Expense Category Methods =============

    async getCategories(
        skip: number = 0,
        limit: number = 100,
        activeOnly: boolean = false,
    ): Promise<PaginatedResponse<ExpenseCategory>> {
        const response = await axios.get(
            `${API_CONFIG.BASE_URL}/expense-categories`,
            {
                params: { skip, limit, active_only: activeOnly },
                ...this.getAuthHeader(),
            },
        );
        return response.data;
    }

    async getActiveCategories(): Promise<ExpenseCategory[]> {
        const response = await this.getCategories(0, 500, true);
        return response.items;
    }

    async createCategory(
        category: ExpenseCategoryCreate,
    ): Promise<ExpenseCategory> {
        const response = await axios.post(
            `${API_CONFIG.BASE_URL}/expense-categories`,
            category,
            this.getAuthHeader(),
        );
        return response.data;
    }

    async updateCategory(
        categoryId: string,
        category: ExpenseCategoryUpdate,
    ): Promise<ExpenseCategory> {
        const response = await axios.put(
            `${API_CONFIG.BASE_URL}/expense-categories/${categoryId}`,
            category,
            this.getAuthHeader(),
        );
        return response.data;
    }

    async deleteCategory(categoryId: string): Promise<void> {
        await axios.delete(
            `${API_CONFIG.BASE_URL}/expense-categories/${categoryId}`,
            this.getAuthHeader(),
        );
    }

    // ============= Expense Methods =============

    async getExpenses(
        skip: number = 0,
        limit: number = 50,
    ): Promise<PaginatedResponse<Expense>> {
        const response = await axios.get(`${API_CONFIG.BASE_URL}/expenses`, {
            params: { skip, limit },
            ...this.getAuthHeader(),
        });
        return response.data;
    }

    async searchExpenses(
        searchParams: ExpenseSearchParams,
        skip: number = 0,
        limit: number = 50,
    ): Promise<PaginatedResponse<Expense>> {
        const response = await axios.post(
            `${API_CONFIG.BASE_URL}/expenses/search`,
            searchParams,
            {
                params: { skip, limit },
                ...this.getAuthHeader(),
            },
        );
        return response.data;
    }

    async getExpense(expenseId: string): Promise<Expense> {
        const response = await axios.get(
            `${API_CONFIG.BASE_URL}/expenses/${expenseId}`,
            this.getAuthHeader(),
        );
        return response.data;
    }

    async createExpense(expense: ExpenseCreate): Promise<Expense> {
        const response = await axios.post(
            `${API_CONFIG.BASE_URL}/expenses`,
            expense,
            this.getAuthHeader(),
        );
        return response.data;
    }

    async updateExpense(
        expenseId: string,
        expense: ExpenseUpdate,
    ): Promise<Expense> {
        const response = await axios.put(
            `${API_CONFIG.BASE_URL}/expenses/${expenseId}`,
            expense,
            this.getAuthHeader(),
        );
        return response.data;
    }

    async deleteExpense(expenseId: string): Promise<void> {
        await axios.delete(
            `${API_CONFIG.BASE_URL}/expenses/${expenseId}`,
            this.getAuthHeader(),
        );
    }

    async getExpenseStatistics(
        searchParams: ExpenseSearchParams,
    ): Promise<ExpenseStatisticsResponse> {
        const response = await axios.post(
            `${API_CONFIG.BASE_URL}/expenses/search/statistics`,
            searchParams,
            this.getAuthHeader(),
        );
        return response.data;
    }

    async exportExpenses(searchParams: ExpenseSearchParams): Promise<Blob> {
        const response = await axios.post(
            `${API_CONFIG.BASE_URL}/expenses/search/export`,
            searchParams,
            {
                ...this.getAuthHeader(),
                responseType: "blob",
            },
        );
        return response.data;
    }
}

export const expenseService = new ExpenseService();
